import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Check, ExternalLink, Handshake } from 'lucide-react';
import { useLang } from './LanguageProvider';
import { useTheme } from './ThemeProvider';
import translations from '../translations';
import { NW_LLC_BAGLANTISI, NW_LOGO } from '../lib/northwest';

/**
 * NORTHWEST İŞ ORTAKLIĞI — ana sayfa bölümü (20 Ekim 2026).
 *
 * Neden ana sayfada ve görünür: Awin, Northwest programındaki üyeliği
 * "website doesn't feature NW" diye askıya alma sürecine aldı. Denetçi Awin'e
 * kayıtlı tanıtım alanına (https://www.whowsell.com) bakıyor; yalnız gizli bir
 * adreste duran sayfa bu denetimi geçmezdi. Ayrıntılı anlatım rehber sayfasında
 * (/us-llc-formation); bu bölüm kısa tanıtım + iki çağrı.
 *
 * Program kuralları (src/lib/northwest.js başlığında): resmi takip bağlantısı,
 * resmi logo görseli (değiştirilmeden), bağlantının yanında affiliate açıklaması
 * ve "A Northwest Registered Agent affiliate" ifadesi.
 */

/**
 * Northwest'e giden her bağlantı bundan geçer: yeni sekme, `sponsored`,
 * `noopener`. `noreferrer` bilerek yok — tıklamanın whowsell.com'dan geldiği
 * Awin raporunda görünsün (tarayıcının varsayılan politikası yalnız alan
 * adını gönderir; sayfa yolu gitmez).
 */
export function NwBaglanti({ href, children, className, ariaYeniSekme }) {
  return (
    <a href={href} target="_blank" rel="sponsored noopener" className={className}>
      {children}
      <span className="sr-only"> ({ariaYeniSekme})</span>
    </a>
  );
}

/**
 * Resmi logo banner'ı (Awin #4844806 / #4844807). Temaya göre YALNIZ biri
 * çizilir: ikisini birden koyup CSS ile gizlemek iki gösterim sayardı.
 * Görsel engellenirse (reklam engelleyici, Awin'e erişim yok) kırık resim
 * kutusu kalmasın diye bağlantı tamamen çekilir; metin çağrıları yerinde
 * kalır. Hata hangi görsele aitse onu gizler — tema değişince diğeri denenir.
 */
export function NwLogo({ alt, ariaYeniSekme, className = '' }) {
  const { theme } = useTheme();
  const logo = theme === 'light' ? NW_LOGO.acik : NW_LOGO.koyu;
  const [hataliSrc, setHataliSrc] = useState(null);
  if (hataliSrc === logo.src) return null;
  return (
    <a
      href={logo.href}
      target="_blank"
      rel="sponsored noopener"
      className={`wh-nw-logo inline-flex items-center ${className}`}
    >
      <img
        src={logo.src}
        width={logo.genislik}
        height={logo.yukseklik}
        alt={alt}
        loading="lazy"
        decoding="async"
        onError={() => setHataliSrc(logo.src)}
        className="block h-auto"
        style={{ width: 'min(100%, 220px)' }}
        draggable="false"
      />
      <span className="sr-only"> ({ariaYeniSekme})</span>
    </a>
  );
}

export default function NorthwestOrtaklik() {
  const { lang } = useLang();
  const t = translations[lang].partner;

  return (
    <section id="partners" data-testid="partner-section" className="py-20 sm:py-24 lg:py-28 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 right-0 w-80 h-80 bg-cyan-500/[0.03] rounded-full blur-3xl" />
      </div>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div
          className="glow-card rounded-2xl p-5 sm:p-8 lg:p-10 grid grid-cols-1 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)] gap-8 lg:gap-12 items-center"
          style={{ '--glow-color': 'rgba(6, 182, 212, 0.3)' }}
        >
          {/* SOL — ne öneriyoruz, neden. */}
          <div className="min-w-0">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full dark:bg-cyan-500/10 bg-cyan-50 border dark:border-cyan-500/20 border-cyan-200 mb-5">
              <Handshake className="w-3.5 h-3.5 dark:text-cyan-300 text-cyan-700" aria-hidden="true" />
              <span className="font-mono text-[11px] tracking-[0.16em] uppercase dark:text-cyan-300 text-cyan-700">{t.badge}</span>
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-4 dark:text-white text-slate-900 break-words">{t.title}</h2>
            <p className="text-sm sm:text-base leading-relaxed dark:text-white/60 text-slate-600 mb-5 break-words">{t.desc}</p>
            <ul className="space-y-2.5">
              {t.items.map((madde) => (
                <li key={madde} className="flex items-start gap-2.5 min-w-0">
                  <Check className="w-4 h-4 mt-0.5 shrink-0 dark:text-emerald-400 text-emerald-600" aria-hidden="true" />
                  <span className="text-[13px] sm:text-sm dark:text-white/70 text-slate-700 break-words">{madde}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* SAĞ — resmi logo, iki çağrı, açıklama. Açıklama bağlantıların
              hemen altında: FTC kuralı "bağlantının olduğu yerde" ister. */}
          <div className="min-w-0 flex flex-col gap-5">
            <NwLogo alt={t.logoAlt} ariaYeniSekme={t.newTab} />
            {/* Geniş ekranda sağ sütun dar: iki düğme yan yana sığmaz, metin
                ikiye bölünür ("step-" / "by-step"). O yüzden lg ve üstünde alt
                alta; tek sütunlu orta genişlikte yan yana. */}
            <div className="flex flex-col sm:flex-row lg:flex-col gap-3">
              <NwBaglanti
                href={NW_LLC_BAGLANTISI}
                ariaYeniSekme={t.newTab}
                className="wh-cta wh-nw-cta group bg-gradient-to-b from-cyan-400 to-cyan-500 hover:from-cyan-300 hover:to-cyan-400 text-slate-950 transition-all shadow-[0_0_0_1px_rgba(6,182,212,0.35),0_10px_30px_rgba(6,182,212,0.25)] active:scale-[0.97]"
              >
                {t.cta}
                <ExternalLink aria-hidden="true" />
              </NwBaglanti>
              <Link
                to="/us-llc-formation"
                className="wh-cta wh-nw-cta group border dark:border-white/15 border-slate-300 dark:text-white text-slate-800 dark:bg-white/[0.04] bg-white hover:dark:bg-white/[0.08] hover:bg-slate-50 transition-all active:scale-[0.97]"
              >
                <BookOpen aria-hidden="true" />
                {t.guide}
                <ArrowRight className="group-hover:translate-x-0.5 transition-transform" aria-hidden="true" />
              </Link>
            </div>
            <div className="space-y-2">
              <p className="text-xs leading-relaxed dark:text-white/45 text-slate-500 break-words">{t.disclosure}</p>
              <p className="text-xs leading-relaxed font-medium dark:text-white/60 text-slate-600 break-words">{t.affiliate}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
